import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { createUserWithProfileViaEdgeFunction } from '../lib/supabase'

interface AddAdminUserModalProps {
  isOpen: boolean
  onClose: () => void
}

interface AddAdminUserFormInputs {
  name: string
  email: string
  password?: string
}

export const AddAdminUserModal: React.FC<AddAdminUserModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddAdminUserFormInputs>()
  const toast = useToast()

  const onSubmit = async (data: AddAdminUserFormInputs) => {
    try {
      console.log('Creating admin user with data:', {
        email: data.email,
        name: data.name,
      })

      const userId = await createUserWithProfileViaEdgeFunction({
        email: data.email,
        password: data.password,
        name: data.name,
        role: 'admin',
      })

      if (!userId) {
        throw new Error('Failed to create user. No user ID returned.')
      }

      toast({
        title: 'Admin user created.',
        description:
          'The new admin user has been successfully created and assigned admin role.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      reset()
      onClose()
    } catch (error: any) {
      console.error('Error creating admin user:', error)

      let errorMessage = 'An unexpected error occurred.'

      if (error.message?.includes('fetch')) {
        errorMessage =
          'Network error: Unable to connect to the server. Please check your internet connection and try again.'
      } else if (error.message?.includes('Failed to create user')) {
        errorMessage =
          'Failed to create user. The email might already be registered or there was a server error.'
      } else if (error.message?.includes('Failed to set admin role')) {
        errorMessage =
          'User was created but failed to set admin permissions. Please try setting admin role manually.'
      } else if (error.message) {
        errorMessage = error.message
      }

      toast({
        title: 'Error creating admin user.',
        description: errorMessage,
        status: 'error',
        duration: 8000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Administrador</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="add-admin-user-form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={4}>
              <FormControl isInvalid={!!errors.name}>
                <FormLabel htmlFor="name">Name</FormLabel>
                <Input
                  id="name"
                  placeholder="Admin Name"
                  {...register('name', { required: 'Name is required' })}
                />
                {errors.name && (
                  <Text color="red.500">{errors.name.message}</Text>
                )}
              </FormControl>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel htmlFor="email">Email</FormLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address',
                    },
                  })}
                />
                {errors.email && (
                  <Text color="red.500">{errors.email.message}</Text>
                )}
              </FormControl>
              <FormControl isInvalid={!!errors.password}>
                <FormLabel htmlFor="password">Password (optional)</FormLabel>
                <Input
                  id="password"
                  type="password"
                  placeholder="Leave blank for auto-generated password"
                  {...register('password')}
                />
                {errors.password && (
                  <Text color="red.500">{errors.password.message}</Text>
                )}
              </FormControl>
            </Stack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose} mr={3}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isLoading={isSubmitting}
            type="submit"
            form="add-admin-user-form"
          >
            Adicionar Admin
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
